import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ScrollText, ArrowLeft } from 'lucide-react';

const TermsOfService = () => {
  const sections = [
    {
      title: '1. Acceptance of Terms',
      content: `By downloading, installing, accessing, or using AppStoreZone ("the Software"), you acknowledge that you have read, understood, and agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you must not download, install, or use the Software.

These Terms constitute a legally binding agreement between you ("User," "you," or "your") and AppStoreZone ("Company," "we," "us," or "our") governing your use of the Software.

We reserve the right to modify these Terms at any time. Your continued use of the Software after any such changes constitutes your acceptance of the new Terms. We encourage you to review these Terms periodically.`
    },
    {
      title: '2. License Grant',
      content: `Subject to your compliance with these Terms, AppStoreZone grants you a limited, non-exclusive, non-transferable, revocable license to:

• Download and install the Software on Windows devices that you own or control
• Use the Software for personal, educational, or commercial purposes
• Make reasonable backup copies of the Software for archival purposes

This license is granted free of charge for the standard features of the Software.

The license granted herein does not include:
• The right to sublicense, sell, resell, or commercially distribute the Software
• The right to modify, adapt, translate, or create derivative works
• The right to reverse engineer, decompile, or disassemble the Software
• The right to remove or alter any proprietary notices or labels

This license is effective until terminated. Your rights under this license will terminate automatically without notice if you fail to comply with any of these Terms.`
    },
    {
      title: '3. User Responsibilities',
      content: `As a user of AppStoreZone, you agree to:

Lawful Use:
• Use the Software only for lawful purposes and in compliance with all applicable laws
• Not use the Software to download, distribute, or access any illegal content
• Not use the Software in any way that could damage, disable, or impair our services

Content Responsibility:
• You are solely responsible for the applications you download and install using the Software
• You must comply with each individual application's terms of service and licensing
• You must not use the Software to infringe on any intellectual property rights

Security:
• Maintain the security of your device and the Software installation
• Not attempt to gain unauthorized access to any systems or networks
• Report any security vulnerabilities you discover to us promptly`
    },
    {
      title: '4. Third-Party Applications',
      content: `AppStoreZone serves as a platform for discovering and downloading third-party applications. We want to make clear:

• We do not develop or own the third-party applications available through our platform
• Each third-party application is subject to its own terms of service and privacy policy
• We make reasonable efforts to ensure the safety of listed applications, but cannot guarantee the behavior of third-party software
• We are not responsible for any damages caused by third-party applications
• The availability of any application through our platform does not constitute our endorsement

By using AppStoreZone to download third-party applications, you acknowledge and accept these limitations.`
    },
    {
      title: '5. Intellectual Property Rights',
      content: `Ownership:
The Software, including all code, graphics, user interface, design elements, documentation, and all intellectual property rights therein, are and shall remain the exclusive property of AppStoreZone and its licensors.

Trademarks:
"AppStoreZone," and all related names, logos, and slogans are trademarks of AppStoreZone. You may not use these trademarks without our prior written permission.

Feedback:
If you provide us with any feedback, suggestions, or ideas regarding the Software, you grant us a non-exclusive, worldwide, royalty-free license to use, modify, and incorporate such feedback into our products and services.`
    },
    {
      title: '6. Disclaimer of Warranties',
      content: `THE SOFTWARE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.

TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, APPSTOREZONE DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:

• IMPLIED WARRANTIES OF MERCHANTABILITY
• FITNESS FOR A PARTICULAR PURPOSE
• NON-INFRINGEMENT
• ACCURACY OR COMPLETENESS OF CONTENT

We do not warrant that:
• The Software will meet your specific requirements
• The Software will be uninterrupted, timely, secure, or error-free
• The results obtained from using the Software will be accurate or reliable
• Any errors in the Software will be corrected

Some jurisdictions do not allow the exclusion of certain warranties, so some of the above exclusions may not apply to you.`
    },
    {
      title: '7. Limitation of Liability',
      content: `TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL APPSTOREZONE, ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, LICENSORS, OR SERVICE PROVIDERS BE LIABLE FOR:

• Any indirect, incidental, special, consequential, or punitive damages
• Loss of profits, revenue, data, use, goodwill, or other intangible losses
• Damages resulting from unauthorized access to or use of the Software
• Damages resulting from any interruption or cessation of the Software
• Damages resulting from any third-party applications downloaded through the Software

WHETHER BASED ON WARRANTY, CONTRACT, TORT (INCLUDING NEGLIGENCE), STRICT LIABILITY, OR ANY OTHER LEGAL THEORY.

IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS EXCEED THE AMOUNT YOU PAID TO US, IF ANY, FOR THE SOFTWARE DURING THE TWELVE (12) MONTHS PRECEDING THE CLAIM.`
    },
    {
      title: '8. Termination',
      content: `Term:
These Terms are effective from your first use of the Software and continue until terminated.

Termination by You:
You may terminate these Terms at any time by uninstalling the Software and destroying all copies in your possession.

Termination by Us:
We may terminate or suspend your license immediately, without prior notice or liability, for any reason, including if you breach these Terms.

Effects of Termination:
Upon termination:
• Your license to use the Software will immediately cease
• You must uninstall the Software and destroy all copies
• Sections of these Terms that by their nature should survive will continue to apply`
    },
    {
      title: '9. Governing Law',
      content: `These Terms shall be governed by and construed in accordance with the laws of the State of Israel, without regard to its conflict of law provisions.

Any dispute arising from or relating to these Terms or the Software shall be resolved through binding arbitration or in the competent courts of Israel.

You agree to resolve disputes on an individual basis and waive any right to participate in class actions.`
    },
    {
      title: '10. Contact Information',
      content: `If you have any questions about these Terms of Service, please contact us:

Email: support@appstorezone.com
Website: https://appstorezone.com

AppStoreZone
Yigal Alon St 94, Tel Aviv–Yafo

Last Updated: March 2026`
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0e17] text-[#ebedfb] font-body">
      {/* Ambient Glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-[20%] left-[20%] w-[500px] h-[500px] rounded-full blur-[160px]" style={{ background: 'radial-gradient(circle, rgba(145,147,255,0.08) 0%, transparent 70%)' }} />
        <div className="absolute bottom-0 right-[20%] w-[400px] h-[400px] rounded-full blur-[160px]" style={{ background: 'radial-gradient(circle, rgba(137,172,255,0.06) 0%, transparent 70%)' }} />
      </div>

      {/* Navbar */}
      <nav className="relative z-50 px-6 py-5" style={{ background: '#0f131d' }}>
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <span className="text-2xl font-extrabold tracking-tight font-display" style={{ letterSpacing: '-0.02em' }}>
              <span className="text-[#ebedfb]">App</span>
              <span className="text-[#89acff]">Store</span>
              <span className="text-[#ebedfb]">Zone</span>
            </span>
          </Link>
          <Link to="/" className="text-[#a7abb7] hover:text-[#ebedfb] transition-colors duration-300 font-medium flex items-center gap-2 text-sm">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </nav>

      {/* Header */}
      <header className="relative z-10 py-16 px-6" style={{ background: '#0f131d' }}>
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ease: [0.2, 0.8, 0.2, 1] }}
            className="flex items-center gap-5"
          >
            <div className="p-4 rounded-xl" style={{ background: '#1a1f2c' }}>
              <ScrollText className="w-8 h-8 text-[#9193ff]" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-extrabold font-display" style={{ letterSpacing: '-0.02em' }}>Terms of Service</h1>
              <p className="text-[#a7abb7] mt-2">Please read these terms carefully before using AppStoreZone.</p>
            </div>
          </motion.div>
        </div>
      </header>

      {/* Content */}
      <main className="relative z-10 py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="space-y-8"
          >
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ delay: index * 0.04, ease: [0.2, 0.8, 0.2, 1] }}
                className="p-8 rounded-2xl"
                style={{ background: '#151925' }}
              >
                <h2 className="text-xl font-bold mb-4 text-[#ebedfb] font-display">{section.title}</h2>
                <div className="text-[#a7abb7] leading-relaxed whitespace-pre-line text-[0.95rem]">
                  {section.content}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 py-14 px-6" style={{ background: '#0f131d' }}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <span className="text-lg font-extrabold tracking-tight font-display" style={{ letterSpacing: '-0.02em' }}>
            <span className="text-[#ebedfb]">App</span>
            <span className="text-[#89acff]">Store</span>
            <span className="text-[#ebedfb]">Zone</span>
          </span>
          <div className="flex gap-8 text-sm text-[#a7abb7]">
            <Link to="/privacy" className="hover:text-[#ebedfb] transition-colors duration-300">Privacy Policy</Link>
            <Link to="/terms" className="text-[#ebedfb]">Terms of Service</Link>
          </div>
          <p className="text-[#444852] text-sm">&copy; {new Date().getFullYear()} AppStoreZone. Yigal Alon St 94, Tel Aviv–Yafo.</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
